import { useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { createCategory, type GlCategory } from '@/services/accountService/glCategoryService';

export type AddGlCategoryPrefill = {
    /** Excel Row 8 */
    nameEn: string;
    /** Excel Row 9 */
    nameZh?: string;
    fsCode?: string;
    /** Sibling leaf under same FS (template for parent/type/code prefix) */
    template?: GlCategory | null;
};

type Props = {
    open: boolean;
    prefill: AddGlCategoryPrefill | null;
    /** All known codes (to avoid collisions in suggestion) */
    existingCodes: Set<string>;
    onClose: () => void;
    onCreated: (created: GlCategory) => void;
};

/** From sibling `R-REV-SER` → parent prefix `R-REV` */
function parentCodePrefix(siblingCode?: string | null): string {
    if (!siblingCode) return '';
    const parts = siblingCode.split('-').filter(Boolean);
    if (parts.length <= 1) return siblingCode;
    return parts.slice(0, -1).join('-');
}

/** Suggest `R-REV-EVT` from header "Events" */
export function suggestLeafGlCode(
    siblingCode: string | undefined,
    headerTitle: string,
    existingCodes: Set<string>,
): string {
    const parent = parentCodePrefix(siblingCode) || 'X-NEW';
    const words = headerTitle.replace(/[^a-zA-Z\s]/g, ' ').trim().split(/\s+/).filter(Boolean);
    let suffix = words.map((w) => w.slice(0, Math.min(3, w.length))).join('').toUpperCase().slice(0, 4);
    if (suffix.length < 2) {
        suffix = headerTitle.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 3) || 'NEW';
    }
    let code = `${parent}-${suffix}`;
    let n = 2;
    while (existingCodes.has(code)) {
        code = `${parent}-${suffix}${n}`;
        n += 1;
    }
    return code;
}

/**
 * Column Mapping 内新建标准 GL 叶子：同 FS 下缺科目时用，避免 -DUP。
 */
export default function MappingAddGlCategoryDialog({
    open, prefill, existingCodes, onClose, onCreated,
}: Props) {
    const [code, setCode] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [nameZh, setNameZh] = useState('');
    const [type, setType] = useState<string>('EXPENSE');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const template = prefill?.template ?? null;
    const fsCode = prefill?.fsCode?.trim() || template?.fsCode || '';

    const derived = useMemo(() => ({
        parentId: template?.parentId ?? null,
        level: 3 as const,
        type: (template?.type || 'EXPENSE').toUpperCase(),
        suggestedCode: suggestLeafGlCode(template?.code, prefill?.nameEn || 'New', existingCodes),
    }), [template, prefill?.nameEn, existingCodes]);

    useEffect(() => {
        if (!open || !prefill) return;
        setNameEn(prefill.nameEn || '');
        setNameZh(prefill.nameZh || '');
        setType(derived.type);
        setCode(derived.suggestedCode);
        setError(null);
    }, [open, prefill, derived.type, derived.suggestedCode]);

    const canSave = !!code.trim() && !!type && !!nameEn.trim();

    const handleSave = async () => {
        if (!canSave) return;
        if (existingCodes.has(code.trim())) {
            setError(`Code "${code.trim()}" already exists — pick another.`);
            return;
        }
        if (!fsCode) {
            setError('FS code is required to create a leaf under this column.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const created = await createCategory({
                code: code.trim(),
                nameEn: nameEn.trim(),
                nameZh: nameZh.trim() || undefined,
                type: type as GlCategory['type'],
                fsCode,
                parentId: derived.parentId ?? undefined,
                level: 3,
                isActive: 1,
            });
            onCreated({
                ...created,
                code: created.code || code.trim(),
                nameEn: created.nameEn || nameEn.trim(),
                nameZh: created.nameZh || nameZh.trim() || undefined,
                fsCode: created.fsCode || fsCode,
                type: (created.type || type) as GlCategory['type'],
                level: 3,
                parentId: created.parentId ?? derived.parentId,
            });
            onClose();
        } catch (e: any) {
            setError(e?.message || 'Failed to create GL category');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add GL Category</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <Typography variant="body2" color="text.secondary">
                        Create a new leaf under FS <strong>{fsCode || '—'}</strong>
                        {template?.code ? <> (template {template.code})</> : null}.
                        Use this when several Excel columns share one FS but need distinct GL codes.
                    </Typography>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                    <TextField
                        size="small"
                        label="Code"
                        required
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        helperText={derived.parentId == null
                            ? 'Tip: pick a sibling GL first so parent can be inferred'
                            : `Parent id: ${derived.parentId}`}
                    />
                    <TextField
                        size="small"
                        label="Name (EN)"
                        required
                        value={nameEn}
                        onChange={(e) => setNameEn(e.target.value)}
                    />
                    <TextField
                        size="small"
                        label="Name (ZH)"
                        value={nameZh}
                        onChange={(e) => setNameZh(e.target.value)}
                    />
                    <TextField
                        select
                        size="small"
                        label="Type"
                        required
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                    >
                        {['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE', 'ADJUSTMENT'].map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                    </TextField>
                    <TextField size="small" label="FS Code" value={fsCode} disabled />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}>
                    {saving ? 'Saving…' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
